import { z } from 'zod';

import type { FlipCardFaceName } from './flipCardFaces.js';
import type { ActivityDefinition } from './types.js';

import { trimmed, wordLimitIssues } from './_checks.js';
import { draftFields, questionFields } from './_shared.js';
import { FLIP_CARD_FACES } from './flipCardFaces.js';
import { activityIssue, asList, asRecord } from './types.js';

const issue = activityIssue('flip_cards');

export const FlipCard = z.looseObject({
  front: z.string().optional(),
  back: z.string().optional(),
  frontContentAssetSlug: z.string().optional(),
  backContentAssetSlug: z.string().optional(),
  outcomeId: z.string().optional(),
});

/**
 * Either face of a card can be an image instead of words. A face with a content-asset slug
 * shows that asset, and its text becomes the image's alt text. The image URL is only ever
 * resolved from the slug when the story is served, so a stored one is an error.
 */
const FACE_CHECKS: Record<FlipCardFaceName, { code: 'cardFront' | 'cardBack'; noText: (n: number) => string }> = {
  front: { code: 'cardFront', noText: n => `Card ${n} has no front.` },
  back: { code: 'cardBack', noText: n => `Card ${n} flips to nothing — it has no back.` },
};

/** What fits on a card face without scrolling. */
const FACE_WORD_LIMIT = 15;

const FACES = FLIP_CARD_FACES.map(entry => ({ ...entry, ...FACE_CHECKS[entry.face] }));

/**
 * Flip cards, in one of two modes, and the mode changes what the activity *is*.
 *
 * In `reveal` (the default) the player flips every card to move on and nothing routes.
 * In `routing` the card they flip is their choice, so each card must name an outcome —
 * a routing deck with an outcome-less card is a choice that leads nowhere, and that is
 * the only difference the checks care about.
 */
export const flipCards: ActivityDefinition = {
  type: 'flip_cards',
  summary: 'A set of cards the player flips — either to read every one, or to choose one and route.',
  setup: z.looseObject({
    ...draftFields,
    ...questionFields,
    mode: z.enum(['reveal', 'routing']).optional(),
    cards: z.array(FlipCard).optional(),
  }),
  check: (setup, ctx) => {
    const cards = asList(setup.cards);
    if (!cards.length) {
      return [issue('empty', 'No cards are written — there is nothing to flip.', [...ctx.path, 'setup', 'cards'], ctx)];
    }

    const routing = trimmed(setup.mode) === 'routing';
    const issues = [];
    // Cards lay out two to a row; an odd deck leaves a hole in the grid.
    if (cards.length % 2 === 1) {
      issues.push(issue('oddCards', `An odd number of cards (${cards.length}) leaves a gap in the two-column grid; write an even number.`, [...ctx.path, 'setup', 'cards'], ctx, cards.length, 'warning'));
    }

    for (const [index, raw] of cards.entries()) {
      const card = asRecord(raw);
      const path = [...ctx.path, 'setup', 'cards', index];

      for (const { face, slugKey, urlKey, code, noText } of FACES) {
        if (card[urlKey] !== undefined) {
          issues.push(issue(
            'cardImageUrl',
            `Card ${index + 1}'s ${face} stores an image URL; pick its image from the content assets instead.`,
            [...path, urlKey],
            ctx,
            card[urlKey],
          ));
        }
        issues.push(...wordLimitIssues({ type: 'flip_cards', text: card[face], limit: FACE_WORD_LIMIT, what: `Card ${index + 1}'s ${face}`, path: [...path, face], ctx }));
        if (!trimmed(card[face])) {
          const message = trimmed(card[slugKey]) ? `Card ${index + 1}'s ${face} image has no alt text.` : noText(index + 1);
          issues.push(issue(code, message, [...path, face], ctx, raw));
        }
      }

      if (!routing) {
        continue;
      }

      const outcomeId = trimmed(card.outcomeId);
      if (!outcomeId) {
        issues.push(issue(
          'cardOutcome',
          `Card ${index + 1} names no outcome, and in routing mode flipping it is the player's choice.`,
          [...path, 'outcomeId'],
          ctx,
          raw,
        ));
        continue;
      }
      if (ctx.outcomeIds && !ctx.outcomeIds.includes(outcomeId)) {
        issues.push(issue(
          'unknownOutcome',
          `Card ${index + 1} routes to outcome '${outcomeId}', which is not one of the node's outcomes (${ctx.outcomeIds.join(', ') || 'none'}).`,
          [...path, 'outcomeId'],
          ctx,
          outcomeId,
        ));
      }
    }

    return issues;
  },
};
